import React from "react";

/**
 * Hardcoded SVG icons for highlight items
 * These are the only icons available for selection in the highlights section
 */

export interface IconOption {
  value: string;
  label: string;
  icon: React.ReactNode;
}

/**
 * Heart Handshake Icon SVG
 */
const HeartHandshakeIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="icon icon-tabler icons-tabler-outline icon-tabler-heart-handshake"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
    <path d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" />
    <path d="M12 6l-3.293 3.293a1 1 0 0 0 0 1.414l.543 .543c.69 .69 1.81 .69 2.5 0l1 -1a3.182 3.182 0 0 1 4.5 0l2.25 2.25" />
    <path d="M12.5 15.5l2 2" />
    <path d="M15 13l2 2" />
  </svg>
);

/**
 * Person Icon SVG
 */
const PersonIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="icon icon-tabler icons-tabler-outline icon-tabler-user"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
    <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
    <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" />
  </svg>
);

/**
 * Barcode Icon SVG
 */
const BarcodeIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="icon icon-tabler icons-tabler-outline icon-tabler-barcode"
  >
    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
    <path d="M4 7v-1a2 2 0 0 1 2 -2h2" />
    <path d="M4 17v1a2 2 0 0 0 2 2h2" />
    <path d="M16 4h2a2 2 0 0 1 2 2v1" />
    <path d="M16 20h2a2 2 0 0 0 2 -2v-1" />
    <path d="M5 11h1v2h-1z" />
    <path d="M10 11l0 2" />
    <path d="M14 11h1v2h-1z" />
    <path d="M19 11l0 2" />
  </svg>
);

/**
 * Available icon options for highlights
 * Icon values must match API enum: IconHeartHandshake, IconUser, IconBarcode
 */
export const ICON_OPTIONS: IconOption[] = [
  {
    value: "IconHeartHandshake",
    label: "Heart Handshake",
    icon: <HeartHandshakeIcon size={16} />,
  },
  {
    value: "IconUser",
    label: "Person",
    icon: <PersonIcon size={16} />,
  },
  {
    value: "IconBarcode",
    label: "Barcode",
    icon: <BarcodeIcon size={16} />,
  },
];

/**
 * Default icon value (must match API enum)
 */
export const DEFAULT_ICON = "IconHeartHandshake";

/**
 * Get icon component by name
 * Handles both old format (for backward compatibility) and new API format
 */
export const getIconComponent = (
  iconName?: string,
  size: number = 20,
): React.ReactNode => {
  switch (iconName) {
    case "IconHeartHandshake":
    case "HeartHandshake": // Backward compatibility
      return <HeartHandshakeIcon size={size} />;
    case "IconUser":
    case "Person": // Backward compatibility
      return <PersonIcon size={size} />;
    case "IconBarcode":
    case "Barcode": // Backward compatibility
      return <BarcodeIcon size={size} />;
    default:
      return <HeartHandshakeIcon size={size} />;
  }
};
